import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ShopLayout from './ShopLayout.js';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';
import { useCart } from '../../contexts/CartContext.js';
import { ordersApi } from '../../services/subscriberApi.js';

const FLARE_RED = '#c4151c';

const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

const formatPKR = (n) => `Rs ${(Number(n) || 0).toLocaleString('en-PK')}`;

function statusTone(kind, value) {
  if (kind === 'payment') {
    if (value === 'Paid') return 'bg-emerald-100 text-emerald-800';
    if (value === 'Pending') return 'bg-amber-100 text-amber-900';
    return 'bg-gray-100 text-gray-700';
  }
  if (value === 'Delivered') return 'bg-emerald-100 text-emerald-800';
  if (value === 'Shipped') return 'bg-indigo-100 text-indigo-800';
  if (value === 'Processing') return 'bg-amber-100 text-amber-900';
  return 'bg-gray-100 text-gray-700';
}

export default function OrderThankYouSection() {
  const brand = useSiteBrand();
  const { clearCart } = useCart();
  const { orderNumber: orderNumberParam } = useParams();
  const location = useLocation();
  const state = location.state || {};
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(state.order || null);
  const [loadError, setLoadError] = useState('');

  const routeOrderNumber = decodeURIComponent(orderNumberParam || '');
  const paidQuery = new URLSearchParams(location.search).get('paid');

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const fromState = state.order;
      const stateLooksComplete = fromState && fromState.delivery_type && (fromState.order_number || fromState.id);
      if (stateLooksComplete && String(fromState.order_number || fromState.id) === String(routeOrderNumber)) {
        if (!cancelled) {
          setOrder(fromState);
          setLoading(false);
        }
        return;
      }
      if (!routeOrderNumber) {
        if (!cancelled) {
          setLoading(false);
          setLoadError('Order number missing.');
        }
        return;
      }
      setLoading(true);
      try {
        const r = await ordersApi.getPublicByNumber(routeOrderNumber);
        const fetched = r?.data?.order || null;
        if (!cancelled) {
          if (!fetched) setLoadError('Order not found.');
          setOrder(fetched);
        }
      } catch (e) {
        if (!cancelled) {
          // Keep partial state if API fails (e.g. older deploy).
          if (fromState?.order_number || fromState?.delivery_type) {
            setOrder(fromState);
          } else {
            setLoadError(e?.response?.data?.message || e?.message || 'Could not load this order.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    bootstrap();
    return () => { cancelled = true; };
  }, [routeOrderNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paidQuery === '1' || order?.payment_status === 'Paid') {
      clearCart();
      try { sessionStorage.removeItem('flare_checkout_draft_v1'); } catch { /* ignore */ }
    }
  }, [paidQuery, order?.payment_status, clearCart]);

  const orderNumber = routeOrderNumber || order?.order_number || order?.id || '';
  const email = order?.guest_email || state.guest_email || '';
  const paymentStatus = order?.payment_status || 'Pending';
  const orderStatus = order?.order_status || 'Pending';
  const deliveryType = order?.delivery_type || state.delivery_type || '';
  const paymentMethod = order?.payment_method || state.payment_method || 'BankTransfer';
  const amount = state.total_amount_pkr ?? order?.total_amount_pkr ?? order?.unit_price_pkr ?? 0;
  const isPhysical = deliveryType === 'Physical';
  const isBankTransfer = String(paymentMethod).toLowerCase().includes('bank');
  const paymentFailed = paidQuery === '0'
    || orderStatus === 'Cancelled'
    || paymentStatus === 'Rejected';
  const issueLabel = order?.magazine_issue?.title
    || order?.magazine_issue?.month_year
    || order?.subscription_plan?.name
    || '';

  const whatsappDigits = String(brand.whatsapp || brand.phone || '')
    .replace(/[^0-9]/g, '')
    .replace(/^0/, '92');
  const whatsappHref = whatsappDigits
    ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(`Hi Flare, I just placed order ${orderNumber || ''}.`)}`
    : null;

  const copyOrderNumber = async () => {
    if (!orderNumber) return;
    try {
      await navigator.clipboard.writeText(String(orderNumber));
      setCopied(true);
      toast.success('Order number copied');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info(String(orderNumber));
    }
  };

  const steps = [
    {
      label: 'Order received',
      done: true,
      desc: orderNumber ? `${orderNumber} is on file` : 'Your checkout was submitted',
    },
    {
      label: isBankTransfer ? 'Payment verification' : 'Payment confirmation',
      done: paymentStatus === 'Paid',
      active: paymentStatus === 'Pending',
      desc: paymentStatus === 'Paid'
        ? 'Payment verified'
        : isBankTransfer
          ? 'Our team will match your transfer screenshot & reference'
          : `Waiting on ${paymentMethod}`,
    },
    {
      label: isPhysical ? 'Print & dispatch' : 'Digital delivery',
      done: orderStatus === 'Delivered' || orderStatus === 'Shipped',
      active: paymentStatus === 'Paid' && (orderStatus === 'Processing' || orderStatus === 'Pending'),
      desc: isPhysical
        ? 'Printed, booked with courier, then tracking by email/SMS'
        : 'PDF link emailed after payment is verified',
    },
  ];

  if (loading) {
    return (
      <ShopLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center text-gray-600 font-semibold">
          Loading your order…
        </div>
      </ShopLayout>
    );
  }

  if (loadError && !order) {
    return (
      <ShopLayout>
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-black text-gray-900 mb-2">Order not found</h1>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <Link to="/magazines" className="inline-flex px-5 py-3 rounded-xl text-white font-bold" style={{ backgroundColor: FLARE_RED }}>
            Browse magazines
          </Link>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-72 opacity-90"
          style={{ background: 'linear-gradient(180deg, #fff1f2 0%, #ffffff 70%)' }}
          aria-hidden
        />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-20">
          <div className="text-center mb-8 sm:mb-10">
            <div
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border-4 border-white shadow-lg"
              style={{
                backgroundColor: paymentFailed
                  ? '#dc2626'
                  : paymentStatus === 'Paid'
                    ? '#059669'
                    : '#d97706',
              }}
            >
              <Icon
                path={paymentFailed ? 'M6 18L18 6M6 6l12 12' : paymentStatus === 'Paid' ? 'M5 13l4 4L19 7' : 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'}
                className="h-8 w-8 text-white"
              />
            </div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] mb-3" style={{ color: FLARE_RED }}>
              {paymentFailed
                ? 'Payment not completed'
                : paymentStatus === 'Paid'
                  ? 'Order confirmed'
                  : 'Awaiting payment'}
            </p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 leading-tight">
              {paymentFailed
                ? 'JazzCash payment did not go through'
                : paymentStatus === 'Paid'
                  ? 'Thank you — we\'ve got your order'
                  : 'Order saved — finish payment to confirm'}
            </h1>
            <p className="mt-3 text-base text-gray-600 max-w-xl mx-auto leading-relaxed">
              {paymentFailed ? (
                <>
                  No charge was captured. Your cart and details are saved on checkout — you can pay again without starting from scratch.
                </>
              ) : paymentStatus === 'Paid' && email ? (
                <>
                  A confirmation is on its way to{' '}
                  <span className="font-bold text-gray-900">{email}</span>.
                  {' '}Save your order number below for support and tracking.
                </>
              ) : paymentStatus === 'Paid' ? (
                <>We&apos;ve received your order successfully. Save your order number below for support and tracking.</>
              ) : (
                <>
                  This order is still unpaid. Complete JazzCash / bank transfer before we process dispatch.
                  {email ? <> We will email <span className="font-bold text-gray-900">{email}</span> after payment.</> : null}
                </>
              )}
            </p>
            {paymentFailed ? (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/checkout"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-md"
                  style={{ backgroundColor: FLARE_RED }}
                >
                  Back to checkout · try again
                </Link>
                <Link to="/magazines" className="inline-flex px-5 py-3 rounded-xl border border-gray-200 bg-white font-bold text-gray-800">
                  Browse magazines
                </Link>
              </div>
            ) : null}
          </div>

          <div className="rounded-3xl bg-white border border-gray-100 shadow-xl overflow-hidden">
            <div className="px-5 sm:px-8 py-6 border-b border-gray-100 bg-gradient-to-br from-rose-50/80 via-white to-slate-50">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">
                    Order number
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <div className="text-2xl sm:text-3xl font-black tracking-tight break-all" style={{ color: FLARE_RED }}>
                      {orderNumber || 'CONFIRMED'}
                    </div>
                    {orderNumber ? (
                      <button
                        type="button"
                        onClick={copyOrderNumber}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-[11px] font-black uppercase tracking-wider text-gray-700 hover:bg-gray-50"
                      >
                        <Icon path="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" className="h-3.5 w-3.5" />
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    ) : null}
                  </div>
                  {issueLabel ? (
                    <div className="mt-2 text-sm font-semibold text-gray-600">{issueLabel}</div>
                  ) : null}
                </div>
                <div className="sm:text-right">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">
                    Amount due
                  </div>
                  <div className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">
                    {formatPKR(amount)}
                  </div>
                  {paymentStatus === 'Pending' ? (
                    <div className="mt-1 text-xs font-semibold text-amber-800">
                      Pending verification
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="p-5 sm:p-8 space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Payment</div>
                  <div className="font-extrabold text-gray-900">{paymentMethod === 'BankTransfer' ? 'Bank Transfer' : paymentMethod}</div>
                  <span className={`mt-2 inline-block px-2 py-0.5 rounded-md text-[11px] font-bold ${statusTone('payment', paymentStatus)}`}>
                    {paymentStatus}
                  </span>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Delivery</div>
                  <div className="font-extrabold text-gray-900">
                    {deliveryType === 'Physical' ? 'Printed copy' : deliveryType === 'Digital' ? 'Digital PDF' : '—'}
                  </div>
                  <span className={`mt-2 inline-block px-2 py-0.5 rounded-md text-[11px] font-bold ${statusTone('order', orderStatus)}`}>
                    {orderStatus}
                  </span>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Support</div>
                  <div className="font-extrabold text-gray-900 truncate">{brand.phone || 'Flare desk'}</div>
                  <div className="mt-2 text-[11px] font-semibold text-gray-500 truncate">{brand.email || '—'}</div>
                </div>
              </div>

              <div>
                <h2 className="text-sm font-black uppercase tracking-[0.14em] text-gray-500 mb-5">What happens next</h2>
                <ol className="relative border-l-2 border-gray-100 ml-3 space-y-7">
                  {steps.map((s, idx) => (
                    <li key={s.label} className="ml-6">
                      <span
                        className={`absolute -left-[11px] flex items-center justify-center w-6 h-6 rounded-full font-extrabold text-xs border-4 ${
                          s.done
                            ? 'bg-emerald-500 text-white border-white ring-2 ring-emerald-200'
                            : s.active
                              ? 'bg-white text-gray-900 border-gray-900 ring-2 ring-gray-100'
                              : 'bg-white text-gray-400 border-gray-200 ring-2 ring-gray-50'
                        }`}
                      >
                        {s.done ? <Icon path="M5 13l4 4L19 7" className="h-3 w-3" /> : idx + 1}
                      </span>
                      <h3 className={`font-extrabold text-base ${s.done || s.active ? 'text-gray-900' : 'text-gray-400'}`}>
                        {s.label}
                      </h3>
                      <p className={`mt-1 text-sm leading-relaxed ${s.done || s.active ? 'text-gray-600' : 'text-gray-400'}`}>
                        {s.desc}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-5">
                <div className="flex gap-3">
                  <div
                    className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white"
                    style={{ backgroundColor: FLARE_RED }}
                  >
                    <Icon path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 text-sm text-rose-950">
                    <div className="font-extrabold mb-1.5">Good to know</div>
                    <ul className="space-y-1.5 font-medium text-rose-900/85 leading-relaxed">
                      {isBankTransfer && paymentStatus === 'Pending' ? (
                        <li>
                          Keep your bank reference / screenshot handy — verification usually completes within one business day.
                        </li>
                      ) : null}
                      {isPhysical ? (
                        <>
                          <li>After payment is marked paid, we print and book courier (typically within 24 working hours).</li>
                          <li>You&apos;ll get tracking by email / SMS once the parcel is handed to the courier.</li>
                        </>
                      ) : (
                        <li>Your digital PDF link is emailed as soon as payment is verified.</li>
                      )}
                      <li>
                        Questions? Quote <span className="font-black">{orderNumber || 'your order number'}</span>
                        {brand.phone ? <> on {brand.phone}</> : null}
                        {brand.email ? <> or {brand.email}</> : null}.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                  to="/magazines"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-extrabold shadow-md hover:opacity-95 transition-opacity"
                  style={{ backgroundColor: FLARE_RED }}
                >
                  <Icon path="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" className="h-5 w-5" />
                  Browse more magazines
                </Link>
                {whatsappHref ? (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 font-extrabold hover:bg-emerald-100 transition-colors"
                  >
                    <Icon path="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" className="h-5 w-5" />
                    WhatsApp us about this order
                  </a>
                ) : (
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-gray-200 bg-white text-gray-800 font-extrabold hover:bg-gray-50 transition-colors"
                  >
                    Back to home
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ShopLayout>
  );
}
